import React, { useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  ValidatedPanel,
  ValidatedFixedPanel,
  getPanelErrorProps,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useVideoAdsCopilot } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { TaskManager } from '@bingads-webui-universal/task-manager';
import {
  ImageListPortalContainerWithControlErrorHandling,
  LogoListPortalContainerWithControlErrorHandling,
  ImageListSummaryCardDetails,
  hasImages,
  PerfMaxImageSuggestionCarousel,
} from '@bingads-webui-campaign-react/pmax-image-experience';
import {
  ValidatedStoreBackedBusinessName,
  HeadlineAssetCollection,
  LongHeadlineAssetCollection,
  DescriptionAssetCollection,
} from '@bingads-webui-campaign-react/pmax-text-input';
import {
  TextAssetsImg,
  ImageAssetsImg,
  LogoAssetsImg,
  BusinessNameImg,
  CallToActionImg,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { VideoPanel } from '@bingads-webui-campaign-react/step-wizard-controls';
import { ValidatedLegacyCallToAction } from '@bingads-webui-campaign-react/legacy-call-to-action';
import { getHeadlinesCardSummaryDetails, getLongHeadlinesCardSummaryDetails, getDescriptionsCardSummaryDetails, hasTextAssets } from '@bingads-webui-campaign-react/validated-text-collection';
import { entityIs, andComp, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';

import { ERROR_ENTITY } from '../../../shared/model/api-error';
import { getAssetsStyles } from './get-assets-styles';
import { getAssetsPanels } from '../../step-constants';

const entityIsAssetGroup = entityIs(ERROR_ENTITY.AssetGroup);

const assetGroupImageAssetsProp = andComp(entityIsAssetGroup, propertyIs('Images'));
const assetGroupBusinessNameProp = andComp(entityIsAssetGroup, propertyIs('BusinessName'));
const ServerValidatedImageListPortalContainer = withUnvalidatedControlErrors(ImageListPortalContainerWithControlErrorHandling, { filter: assetGroupImageAssetsProp });

export const AssetsSection = ({
  permissions,
  images,
  videos,
  mergedI18n,
  perfMarker,
  businessName,
  headlines,
  longHeadlines,
  descriptions,
  imagesContainerRef,
  imageExperienceAddNewImageCallback,
  setAddNewShortHeadlinesCallback,
  setAddNewLongHeadlinesCallback,
  setAddNewDescriptionCallback,
  imageExperiencePortalContainerState,
  logosContainerRef,
  providerId,
  maxUploadNum,
  locale,
  CallToAction,
  setNewCTACallback,
  setSelectedVideoId,
  selectedVideoId,
}) => {
  const { IsPerformanceMaxAutomatedCTAEnabled, IsDomesticSimplifiedChineseEnabled } = permissions;

  const {
    PerformanceMaxInCnMarket: IsPerformanceMaxInCnMarketEnabled,
    PMaxNewAspectRatios,
    PMaxExcludeNewAspectRatios,
  } = permissions.dynamic;

  const isFeatureBlockedForCnMarket = IsDomesticSimplifiedChineseEnabled && IsPerformanceMaxInCnMarketEnabled;
  const isPMaxAspectRatiosV2Enabled = PMaxNewAspectRatios && !PMaxExcludeNewAspectRatios;

  const i18n = useContext(I18nContext);

  const allPanels = useMemo(() => getAssetsPanels(permissions), [permissions]);

  const [
    allAssetsSections,
    assetsExpandedSections,
    setAssetsExpandedSections,
  ] = useCollapseState(
    allPanels, /* all sections */
    allPanels /* initially expanded sections */
  );

  const allAssetsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAssetsExpandedSections }),
    [setAssetsExpandedSections]
  );

  const headlinesCardSummaryDetails = useMemo(() => getHeadlinesCardSummaryDetails(i18n, headlines), [headlines, i18n]);
  const longHeadlinesCardSummaryDetails = useMemo(() => getLongHeadlinesCardSummaryDetails(i18n, longHeadlines), [longHeadlines, i18n]);
  const descriptionsCardSummaryDetails = useMemo(() => getDescriptionsCardSummaryDetails(i18n, descriptions), [descriptions, i18n]);

  const textAndImageAssetsOptional = useMemo(() => providerId && !hasImages(images) && !hasTextAssets(headlines, longHeadlines, descriptions), [providerId, images, headlines, longHeadlines, descriptions]);

  const classes = useDefaultStyles(getAssetsStyles);

  const { fetch } = useVideoAdsCopilot();

  const copilotFetchTaskManager = useMemo(() => new TaskManager(), []);

  useEffect(() => {
    fetch(copilotFetchTaskManager);
    setClarityTag({ VideoCopilotGeneration: true });
  }, [copilotFetchTaskManager, fetch]);

  const assets = useMemo(() => (
    <div className={classNames(classes.container, 'asset-group-assets-section')}>
      <h3 className="section-title">{i18n.getString(_TL_('Add assets'))}</h3>
      <div className="section-description">
        <span>{i18n.getString(_TL_('Provide your assets and we’ll automatically determine what your ads look like, where and when they appear, and who sees them.'))}</span>
      </div>
      <Collapse allPanelItems={allAssetsSections} checkItems skipItemsTypeCheck activeKeys={assetsExpandedSections} setActiveKeys={setAssetsExpandedSections}>
        <ValidatedPanel
          key="images"
          title={<TextTitle
            titleKey={_TL_('Images')}
            topicKey={isPMaxAspectRatiosV2Enabled ? 'pop_BA_PMAX_UpdatedAspectRatio' : 'pop_BA_ImageAssets_PMAX'}
            icon={ImageAssetsImg}
            iconAltText={i18n.getString(_TL_('"Images" panel\'s image'))}
            details={<ImageListSummaryCardDetails i18n={i18n} images={images} isLogos={false} />}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
              expand: allAssetsExpand,
              expandKey: 'images',
            }}
        >
          <span>{i18n.getString(_TL_('You can add up to 20 images to your ad. We recommend that you add two to three relevant images. Required aspect ratios: 1.91:1 and 1:1. Minimum image upload dimensions: 703 x 368 pixels. '))}</span>
          <ServerValidatedImageListPortalContainer
            i18n={i18n}
            containerRef={imagesContainerRef}
            containerState={imageExperiencePortalContainerState}
            isLogos={false}
          />
          <PerfMaxImageSuggestionCarousel i18n={i18n} addImageCallback={imageExperienceAddNewImageCallback} permissions={permissions} maxUploadNum={maxUploadNum} />
        </ValidatedPanel>
        <ValidatedPanel
          key="logos"
          title={<TextTitle
            titleKey={_TL_('Logos')}
            icon={LogoAssetsImg}
            iconAltText={i18n.getString(_TL_('"Logos" panel\'s image'))}
            topicKey="pop_BA_CampaignCreate_MMA_Logos"
            details={<ImageListSummaryCardDetails i18n={i18n} images={images} isLogos />}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
              expand: allAssetsExpand,
              expandKey: 'logos',
            }}
        >
          <span>{i18n.getString(_TL_('You can add up to 5 different logos. Each logo can have one or both 4:1 aspect ratio and 1:1 aspect ratio.'))}</span>
          <LogoListPortalContainerWithControlErrorHandling
            i18n={i18n}
            containerRef={logosContainerRef}
            containerState={imageExperiencePortalContainerState}
            isLogos
          />
        </ValidatedPanel>

        <VideoPanel
          key="videos"
          perfMarker={perfMarker}
          mergedI18n={mergedI18n}
          videos={videos}
          icon={TextAssetsImg}
          allAssetsExpand={allAssetsExpand}
          topicKey="pop_BA_Apps_Videos"
          setSelectedVideoId={setSelectedVideoId}
          selectedVideoId={selectedVideoId}
        />
        <ValidatedPanel
          key="headlines"
          title={<TextTitle
            titleKey={_TL_('Short headlines')}
            icon={TextAssetsImg}
            iconAltText={i18n.getString(_TL_('Short headlines'))}
            details={headlinesCardSummaryDetails}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
                expand: allAssetsExpand,
                expandKey: 'headlines',
              }}
        >
          <HeadlineAssetCollection isOptional={textAndImageAssetsOptional} showDeleteButton setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback} />
        </ValidatedPanel>

        <ValidatedPanel
          key="long-headlines"
          title={<TextTitle
            titleKey={_TL_('Long headlines')}
            icon={TextAssetsImg}
            iconAltText={i18n.getString(_TL_('Long headlines'))}
            details={longHeadlinesCardSummaryDetails}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
                expand: allAssetsExpand,
                expandKey: 'long-headlines',
              }}
        >
          <LongHeadlineAssetCollection isOptional={textAndImageAssetsOptional} showDeleteButton setAddNewLongHeadlinesCallback={setAddNewLongHeadlinesCallback} />
        </ValidatedPanel>


        <ValidatedPanel
          key="descriptions"
          title={<TextTitle
            titleKey={_TL_('Descriptions')}
            icon={TextAssetsImg}
            iconAltText={i18n.getString(_TL_('Descriptions'))}
            details={descriptionsCardSummaryDetails}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
                expand: allAssetsExpand,
                expandKey: 'descriptions',
              }}
        >
          <DescriptionAssetCollection isOptional={textAndImageAssetsOptional} showDeleteButton setAddNewDescriptionCallback={setAddNewDescriptionCallback} />
        </ValidatedPanel>

        <ValidatedFixedPanel
          key="business-name"
          title={
            <TextTitle
              titleKey={_TL_('Business name')}
              topicKey="pop_BA_CampaignCreate_AudAds_BusinessName"
              icon={BusinessNameImg}
              iconAltText={i18n.getString(_TL_('"Business name" panel\'s image'))}
            />
            }
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        >
          <ValidatedStoreBackedBusinessName
            id="input-business-name"
            providerId={providerId}
            i18n={i18n}
            validationProps={{
                data: { BusinessName: businessName },
                initialValue: { BusinessName: businessName },
              }}
            formErrorProps={{ filter: assetGroupBusinessNameProp }}
          />
        </ValidatedFixedPanel>
        <ValidatedFixedPanel
          key="call-to-action"
          title={
            <TextTitle
              titleKey={_TL_('Call-to-action')}
              topicKey="pop_BA_PMAX_CallToAction"
              icon={CallToActionImg}
              iconAltText={i18n.getString(_TL_('"Call to action" panel\'s image'))}
            />
            }
          noOverflowHidden
          relativePosition
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        >
          <ValidatedLegacyCallToAction
            locale={locale}
            permissions={permissions}
            validationProps={{ data: { CallToAction } }}
            isPmaxAd={IsPerformanceMaxAutomatedCTAEnabled && !isFeatureBlockedForCnMarket}
            setNewCTACallback={setNewCTACallback}
          />
        </ValidatedFixedPanel>
      </Collapse>
    </div>
  ), [classes.container, i18n, allAssetsSections, assetsExpandedSections, setAssetsExpandedSections, isPMaxAspectRatiosV2Enabled, images, allAssetsExpand, imagesContainerRef, imageExperiencePortalContainerState, imageExperienceAddNewImageCallback, permissions, maxUploadNum, logosContainerRef, headlinesCardSummaryDetails, textAndImageAssetsOptional, setAddNewShortHeadlinesCallback, longHeadlinesCardSummaryDetails, setAddNewLongHeadlinesCallback, descriptionsCardSummaryDetails, setAddNewDescriptionCallback, providerId, businessName, locale, CallToAction, IsPerformanceMaxAutomatedCTAEnabled, isFeatureBlockedForCnMarket, setNewCTACallback]);

  return assets;
};

AssetsSection.propTypes = {
  permissions: PropTypes.shape({
    IsPerformanceMaxAutomatedCTAEnabled: PropTypes.bool,
    IsDomesticSimplifiedChineseEnabled: PropTypes.bool,
    dynamic: PropTypes.shape({
      PerformanceMaxInCnMarket: PropTypes.bool,
    }),
  }).isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  businessName: PropTypes.string.isRequired,
  headlines: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  longHeadlines: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  descriptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  imagesContainerRef: PropTypes.shape({}).isRequired,
  imageExperienceAddNewImageCallback: PropTypes.func.isRequired,
  setAddNewShortHeadlinesCallback: PropTypes.func.isRequired,
  setAddNewLongHeadlinesCallback: PropTypes.func.isRequired,
  setAddNewDescriptionCallback: PropTypes.func.isRequired,
  imageExperiencePortalContainerState: PropTypes.shape({}).isRequired,
  logosContainerRef: PropTypes.shape({}).isRequired,
  providerId: PropTypes.string.isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  CallToAction: PropTypes.shape({}).isRequired,
  setNewCTACallback: PropTypes.func.isRequired,
};
